import { OpenAPI } from './core/OpenAPI';
import { config } from '../config/config';
import authService from '../services/authService';

/**
 * Get tenant name from subdomain or app config.
 * Subdomain takes precedence over stored config.
 */
const getTenantName = (): string | null => {
  try {
    const hostname = window.location.hostname;
    const parts = hostname.split('.');

    // If we have a subdomain (more than 2 parts like subdomain.example.com)
    if (parts.length > 2) {
      const subdomain = parts[0];
      // Exclude common non-tenant subdomains. Keep this list aligned with
      // authService.getSubdomainTenant so the __tenant header and the OIDC redirect agree.
      if (subdomain && !['www', 'api', 'app', 'admin', 'partner'].includes(subdomain.toLowerCase())) {
        return subdomain;
      }
    }

    // Special case for localhost development with subdomain format (e.g., supreme.localhost)
    if (hostname.includes('localhost') && parts.length >= 2 && parts[0] !== 'localhost') {
      const subdomain = parts[0];
      if (subdomain && !['www', 'api', 'app', 'admin', 'partner'].includes(subdomain.toLowerCase())) {
        return subdomain;
      }
    }

    // No subdomain detected — this is the host tenant (e.g., admin.routemize.com, routemize.com).
    // Do NOT fall back to cached app_config — it may contain a stale tenant from a previous session
    // which would cause cross-tenant auth leaks when switching subdomains.
  } catch (error) {
    console.error('Error getting tenant name for headers:', error);
  }
  return null;
};

/**
 * Configure the OpenAPI client with base URL and authentication
 */
export const initializeApiClient = () => {
  // Set the base URL from configuration
  OpenAPI.BASE = config.api.baseUrl;

  // Configure authentication token (impersonation token takes priority).
  // getEffectiveAccessToken() is the shared source of truth also used by the
  // SignalR hub, so REST and the hub never authenticate as different principals.
  OpenAPI.TOKEN = async () => {
    const token = await authService.getEffectiveAccessToken();
    return token || undefined;
  };

  // Configure headers with tenant information
  OpenAPI.HEADERS = async () => {
    const headers: Record<string, string> = {};

    // Add tenant header if available (from subdomain or config)
    const tenantName = getTenantName();
    if (tenantName) {
      headers['__tenant'] = tenantName;
    }

    return headers;
  };

  // Enable credentials for cross-origin requests
  OpenAPI.WITH_CREDENTIALS = false;
  OpenAPI.CREDENTIALS = 'include';

  console.log(`API client initialized with base URL: ${OpenAPI.BASE}`);
};
